import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const LegalPage: React.FC = () => {
  const { page } = useParams<{ page: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageMap: Record<string, { title: string; file: string }> = {
    'privacy': {
      title: 'Privacy Policy',
      file: '/privacy-policy.md'
    },
    'terms': {
      title: 'Terms of Service', 
      file: '/terms-of-service.md'
    },
    'health-privacy': {
      title: 'Health Information Privacy',
      file: '/health-information-privacy.md'
    },
    'user-guide': {
      title: 'User Guide',
      file: '/user-guide.md'
    }
  };

  useEffect(() => {
    const loadContent = async () => {
      if (!page || !pageMap[page]) {
        setError('Page not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(pageMap[page].file);
        if (!response.ok) {
          throw new Error('Failed to load content');
        }
        
        const text = await response.text();
        // Simple markdown to HTML conversion (for production, use a proper markdown parser)
        const html = text
          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
          .replace(/^# (.*$)/gim, '<h1>$1</h1>')
          .replace(/^\* (.*$)/gim, '<li>$1</li>')
          .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
          .replace(/\*\*(.*)\*\*/g, '<strong>$1</strong>')
          .replace(/\n\n/g, '</p><p>')
          .replace(/^/, '<p>')
          .replace(/$/, '</p>');
          
        setContent(html);
      } catch (err) {
        setError('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-stone-100 flex items-center justify-center">
        <div className="text-brand-stone-600">Loading...</div>
      </div>
    );
  }

  if (error || !page || !pageMap[page]) {
    return (
      <div className="min-h-screen bg-brand-stone-100">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-brand-stone-800 mb-4">Page Not Found</h1>
          <p className="text-brand-stone-600 mb-4">The requested page could not be found.</p>
          <Link to="/" className="text-brand-green-600 hover:text-brand-green-700">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-stone-100">
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="text-brand-green-800 hover:text-brand-green-600">
            ← Back to Healing with Hailey
          </Link>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
        
        <div className="mt-8 text-center text-brand-stone-500">
          <p>Last updated: {new Date().toLocaleDateString()}</p>
        </div>
      </main>
      
      <footer className="bg-white mt-12 py-8 border-t">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/legal/privacy" className="text-brand-stone-600 hover:text-brand-green-600">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="text-brand-stone-600 hover:text-brand-green-600">
              Terms of Service
            </Link>
            <Link to="/legal/health-privacy" className="text-brand-stone-600 hover:text-brand-green-600">
              Health Information Privacy
            </Link>
            <Link to="/legal/user-guide" className="text-brand-stone-600 hover:text-brand-green-600">
              User Guide
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;